import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Stethoscope,
  Soup,
  Armchair,
  Star,
  CupSoda,
  ThumbsDown,
  Info,
  Plus,
  AlertTriangle,
  CircleCheck,
  PlaneTakeoff,
  PlaneLanding,
} from '@/components/icons';
import { hapticLight } from '@/lib/haptics';
import { Skeleton } from '@/components/ui/skeleton';
import { ProfileAvatar } from './ProfileAvatar';
import { Button } from './ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { usePassengerGetById } from '../lib/hooks';
import { useDocumentGetByPassenger } from '@/lib/hooks';
import { getIso2ByCountryName } from '@/lib/phone';
import { CountryFlag } from './ui/country-flag';
import { PhoneLink, EmailLink } from './ContactLink';
import { PaxPreferencePhotos } from './PaxPreferencePhotos';
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton';
import { useBackTo } from './topbar/useBackTo';
import { AppLayout } from './AppLayout';
import { DesktopPage } from './desktop/DesktopPage';
import { DesktopPageHeader } from './desktop/DesktopPageHeader';
import { useIsDesktop } from './ui/use-mobile';
import { PaxActionsSelect } from './PaxActionsSelect';
import { DocumentFormDrawer } from './documents/DocumentFormDrawer';
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer';
import { DocumentListCard, getPassengerDocumentIcon } from './DocumentListCard';
import { EmptyStateCard } from './EmptyStateCard';
import dayjs from 'dayjs';
import { getInitials } from '../lib/utils';
import { documentSubTypeLabels } from '@@api/shared';
import { useIsAdmin } from '../hooks/useIsCompanyAdmin';

interface PAXDetailViewProps {
  passengerId: string;
  initialTab?: 'details' | 'documents' | 'flight-history';
  /** List route the back button returns to — set by the rendering route. */
  backTo?: '/pax' | '/operations/pax';
}

export function PAXDetailView({ passengerId, initialTab = 'details', backTo = '/pax' }: PAXDetailViewProps) {
  const { isAdmin } = useIsAdmin();
  const [showAddDocDrawer, setShowAddDocDrawer] = useState(false);
  const [viewDocId, setViewDocId] = useState<string | null>(null);
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDocOpen, setIsViewDocOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const { data: passenger, isPending, error } = usePassengerGetById(passengerId);
  const { data: documents } = useDocumentGetByPassenger(passengerId);
  const isDesktop = useIsDesktop();

  const handleBack = useBackTo(backTo);

  const getFullName = () => {
    return `${passenger?.firstName || ''} ${passenger?.lastName || ''}`.trim() || 'Unknown';
  };

  // Group persisted preference photos by preference key for the read-only grids.
  const photosByPreference = (
    (passenger as { preferencePhotos?: { preference: string; s3Key: string }[] } | undefined)
      ?.preferencePhotos ?? []
  ).reduce<Record<string, string[]>>((acc, p) => {
    acc[p.preference] = [...(acc[p.preference] ?? []), p.s3Key];
    return acc;
  }, {});

  const preferencePhotoGrid = (preference: string) => {
    const s3Keys = photosByPreference[preference] ?? [];
    if (s3Keys.length === 0) return undefined;
    return <PaxPreferencePhotos readOnly preference={preference} s3Keys={s3Keys} />;
  };

  if (isPending) {
    const loadingBody = (
      <>
        {/* Profile Card */}
        <Card className="flex items-center justify-center px-6 py-4">
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="size-18 rounded-full" />
            <Skeleton className="h-5 w-32" />
          </div>
        </Card>

        {/* Info Cards */}
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="px-4 py-4">
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 items-center">
                  <Skeleton className="size-8 rounded-full shrink-0" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
            </Card>
          ))}
        </div>
      </>
    );

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title="Passenger" />
          {loadingBody}
        </DesktopPage>
      );
    }

    return (
      <AppLayout
        topBar={<TopBarBackIconButton onBack={handleBack} />}
        className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
      >
        {loadingBody}
      </AppLayout>
    );
  }

  if (error || !passenger) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <p className="text-content-primary-subtle mb-4">Passenger not found</p>
        <Button variant="outline" onClick={handleBack}>
          Go Back
        </Button>
      </div>
    );
  }

  const fullName = getFullName();
  const totalFlights = passenger.tripLegs?.length || 0;

  // Breadcrumb follows the entry point: the operations route gets the full
  // Operations / Passengers trail, the crew-facing /pax route just Passengers.
  const paxBreadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleBack}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to passengers"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        {backTo === '/operations/pax' && (
          <>
            <Link to="/operations" className="hover:underline">
              Operations
            </Link>
            <span className="text-content-primary-subtle"> / </span>
          </>
        )}
        <Link to={backTo} className="hover:underline">
          Passengers
        </Link>
        <span className="text-content-primary-subtle"> / {fullName}</span>
      </span>
    </span>
  );

  const isDocExpiringSoon = (expiryDate: Date | string) => {
    const expiry = dayjs(expiryDate);
    const now = dayjs();
    return expiry.diff(now, 'day') <= 30 && expiry.isAfter(now);
  };

  const isDocExpired = (expiryDate: Date | string) => {
    return dayjs(expiryDate).isBefore(dayjs());
  };

  // Basic view for non-admin users
  if (!isAdmin) {
    const basicBody = (
      <>
        {/* Profile Card */}
          <Card className="flex items-center justify-center px-6 py-4">
            <div className="flex flex-col items-center gap-3">
              <ProfileAvatar
                className="size-18"
                photoS3Key={passenger.photoS3Key}
                avatarColor={passenger.avatarColor}
                initials={getInitials(passenger.firstName, passenger.lastName)}
                fallbackClassName="text-4xl font-semibold text-background"
              />
              <h3>{fullName}</h3>
            </div>
          </Card>

          {/* Info Cards */}
          <div className="flex flex-col gap-3">
            {/* Basic Info Card */}
            <Card className="px-4 py-4">
              <div className="flex flex-col gap-4">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium">Name</span>
                  <span>{fullName}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium">Nationality</span>
                  <span className="inline-flex items-center gap-1.5">
                    {passenger.nationality ? (
                      <>
                        <CountryFlag
                          iso2={getIso2ByCountryName(passenger.nationality)}
                          className="w-4"
                        />
                        {passenger.nationality}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium">Date of Birth</span>
                  <span>
                    {passenger.dateOfBirth
                      ? dayjs.utc(passenger.dateOfBirth).format('MMM D')
                      : '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-medium">Role</span>
                  <span>{passenger.role || '-'}</span>
                </div>
              </div>
            </Card>

            {/* Medical Information Card */}
            <PreferenceCard
              icon={<Stethoscope className="size-4 text-content-brand-bold" />}
              title="Medical Information"
              value={[passenger.medicalInfo, passenger.allergies].filter(Boolean).join('\n') || '-'}
              photos={preferencePhotoGrid('medicalInfo')}
            />

            {/* Food Preferences Card */}
            <PreferenceCard
              icon={<Soup className="size-4 text-content-brand-bold" />}
              title="Food Preferences"
              value={passenger.foodPreferences || '-'}
              photos={preferencePhotoGrid('foodPreferences')}
            />

            {/* Cabin Preferences Card */}
            <PreferenceCard
              icon={<Armchair className="size-4 text-content-brand-bold" />}
              title="Cabin Preferences"
              value={passenger.cabinPreferences || '-'}
              photos={preferencePhotoGrid('cabinPreferences')}
            />

            {/* Beauty Preferences Card */}
            <PreferenceCard
              icon={<Star className="size-4 text-content-brand-bold" />}
              title="Beauty Preferences"
              value={passenger.beautyPreferences || '-'}
              photos={preferencePhotoGrid('beautyPreferences')}
            />

            {/* Beverage Preferences Card */}
            <PreferenceCard
              icon={<CupSoda className="size-4 text-content-brand-bold" />}
              title="Beverage Preferences"
              value={passenger.beveragePreferences || '-'}
              photos={preferencePhotoGrid('beveragePreferences')}
            />

            {/* Dislikes Card */}
            <PreferenceCard
              icon={<ThumbsDown className="size-4 text-content-brand-bold" />}
              title="Dislikes"
              value={passenger.dislikes || '-'}
              photos={preferencePhotoGrid('dislikes')}
            />

            {/* General Information Card */}
            <PreferenceCard
              icon={<Info className="size-4 text-content-brand-bold" />}
              title="General Information"
              value={passenger.generalInformation || '-'}
              photos={preferencePhotoGrid('generalInformation')}
            />
          </div>

          {/* Public Documents */}
          {(documents?.length ?? 0) > 0 && (
            <div className="flex flex-col gap-3 mt-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.5px]">
                <span className="text-content-primary-subtle-mid">Documents</span>
              </div>
              {documents!.map((doc) => {
                const DocumentIcon = getPassengerDocumentIcon(doc.subType);
                return (
                  <DocumentListCard
                    key={doc.id}
                    icon={<DocumentIcon />}
                    title={doc.title}
                    subTypeLabel={documentSubTypeLabels[doc.subType] || doc.subType}
                    expiryLabel={
                      doc.expiryDate
                        ? `Expires ${dayjs(doc.expiryDate).format('MMM D, YYYY')}`
                        : undefined
                    }
                    expiryStatus={
                      doc.expiryDate
                        ? isDocExpired(doc.expiryDate)
                          ? 'expired'
                          : isDocExpiringSoon(doc.expiryDate)
                            ? 'expiring'
                            : 'valid'
                        : undefined
                    }
                    isPublic={doc.isPublic}
                    onClick={() => {
                      setViewDocId(doc.id);
                      setIsViewDocOpen(true);
                    }}
                  />
                );
              })}
            </div>
          )}

          <ViewDocumentDrawer
            isOpen={isViewDocOpen}
            onClose={() => setIsViewDocOpen(false)}
            documentId={viewDocId}
            readOnly
          />
      </>
    );

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title={fullName} breadcrumb={paxBreadcrumb} />
          {basicBody}
        </DesktopPage>
      );
    }

    return (
      <AppLayout
        topBar={<TopBarBackIconButton onBack={handleBack} />}
        className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
      >
        {basicBody}
      </AppLayout>
    );
  }

  const adminBody = (
    <>
        {/* Deleted Banner */}
        {passenger.isDeleted && (
          <div className="bg-content-negative-bold/10 border border-border-negative-bold/20 rounded-lg px-4 py-2">
            <p className="text-xs font-medium text-content-negative-bold text-center">This passenger has been deleted</p>
          </div>
        )}

        {/* Profile Card */}
        <Card className={`flex gap-8 items-center px-6 py-4 ${passenger.isDeleted ? 'opacity-60' : ''}`}>
          {/* Left - Avatar and Name */}
          <div className="flex-1 flex flex-col items-center gap-3">
            <ProfileAvatar
              className="size-18"
              photoS3Key={passenger.photoS3Key}
              avatarColor={passenger.avatarColor}
              initials={getInitials(passenger.firstName, passenger.lastName)}
              fallbackClassName="text-[35px] font-semibold text-background"
            />
            <p className="text-base font-semibold">{fullName}</p>
          </div>

          {/* Right - Flight Stats */}
          <div className="flex flex-col gap-2 w-[114px]">
            <div className="flex flex-col">
              <p className="text-xl font-semibold">{totalFlights}</p>
              <p className="text-xs font-medium text-content-primary-subtle tracking-[0.18px] leading-[1.5]">Total Flights</p>
            </div>
            <div className="flex flex-col">
              <p className="text-xl font-semibold">{Math.round(passenger.totalFlightHours || 0)}</p>
              <p className="text-xs font-medium text-content-primary-subtle tracking-[0.18px] leading-[1.5]">Total Hours</p>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          <TabsList className="w-full mb-4">
            <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
            <TabsTrigger value="documents" className="flex-1">Documents</TabsTrigger>
            <TabsTrigger value="flight-history" className="flex-1">Flight History</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="flex flex-col gap-4">
              {/* Basic Info Card */}
              <Card className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Name</span>
                    <span>{fullName}</span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Nationality</span>
                    <span className="inline-flex items-center gap-1.5">
                      {passenger.nationality ? (
                        <>
                          <CountryFlag
                            iso2={getIso2ByCountryName(passenger.nationality)}
                            className="w-4"
                          />
                          {passenger.nationality}
                        </>
                      ) : (
                        '-'
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Telephone</span>
                    <PhoneLink phone={passenger.phone} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Email</span>
                    <EmailLink email={passenger.email} />
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Date of Birth</span>
                    <span>
                      {passenger.dateOfBirth
                        ? dayjs.utc(passenger.dateOfBirth).format('MMM D, YYYY')
                        : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs tracking-[0.18px]">
                    <span className="font-medium">Role</span>
                    <span>{passenger.role || '-'}</span>
                  </div>
                </div>
              </Card>

              {/* Medical Information Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Stethoscope className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Medical Information</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">
                    {[passenger.medicalInfo, passenger.allergies].filter(Boolean).join('\n') || '-'}
                  </p>
                  {preferencePhotoGrid('medicalInfo')}
                </div>
              </Card>

{/* Food Preferences Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Soup className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Food Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.foodPreferences || '-'}</p>
                  {preferencePhotoGrid('foodPreferences')}
                </div>
              </Card>

              {/* Cabin Preferences Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Armchair className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Cabin Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.cabinPreferences || '-'}</p>
                  {preferencePhotoGrid('cabinPreferences')}
                </div>
              </Card>

              {/* Beauty Preferences Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Star className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Beauty Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.beautyPreferences || '-'}</p>
                  {preferencePhotoGrid('beautyPreferences')}
                </div>
              </Card>

              {/* Beverage Preferences Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <CupSoda className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Beverage Preferences</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.beveragePreferences || '-'}</p>
                  {preferencePhotoGrid('beveragePreferences')}
                </div>
              </Card>

              {/* Dislikes Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <ThumbsDown className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">Dislikes</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.dislikes || '-'}</p>
                  {preferencePhotoGrid('dislikes')}
                </div>
              </Card>

              {/* General Information Card */}
              <Card className="px-4 py-4">
                <div className="flex flex-col gap-4">
                  <div className="flex gap-2 items-center">
                    <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
                      <Info className="size-4 text-content-brand-bold" />
                    </div>
                    <p className="text-sm font-medium">General Information</p>
                  </div>
                  <p className="text-xs whitespace-pre-wrap wrap-break-word">{passenger.generalInformation || '-'}</p>
                  {preferencePhotoGrid('generalInformation')}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div>
              {!documents || documents.length === 0 ? (
                <EmptyStateCard
                  label="Add a document"
                  onAdd={() => {
                    hapticLight()
                    setShowAddDocDrawer(true)
                  }}
                />
              ) : (
                <>
                  <div className="flex flex-col gap-3">
                    {documents.map((doc) => {
                      const expired = isDocExpired(doc.expiryDate);
                      const expiringSoon = !expired && isDocExpiringSoon(doc.expiryDate);
                      const DocIcon = getPassengerDocumentIcon(doc.subType);

                      return (
                        <Card
                          key={doc.id}
                          interactive
                          className="px-4 py-4 flex items-center justify-between cursor-pointer active:bg-surface-L2/50 transition-colors"
                          onClick={() => {
                            setViewDocId(doc.id);
                            setIsViewDocOpen(true);
                          }}
                        >
                          <div className="flex items-center gap-4">
                            {/* Document Icon */}
                            <DocIcon className="size-4 text-content-brand-bold shrink-0" />

                            {/* Document Info */}
                            <div className="flex flex-col gap-2">
                              <p className="text-sm font-medium">{doc.title}</p>
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="border-border-L2 rounded-lg">
                                  {documentSubTypeLabels[doc.subType] || doc.subType}
                                </Badge>
                                <Badge variant="outline" className="border-border-L2 rounded-lg">
                                  Expires {dayjs(doc.expiryDate).format('MMM D, YYYY')}
                                </Badge>
                                <Badge variant="outline" className="border-border-L2 rounded-lg">
                                  {doc.isPublic ? 'Public' : 'Private'}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {/* Status Indicator */}
                          {expired ? (
                            <div className="size-8 rounded-full bg-content-negative-bold/10 flex items-center justify-center shrink-0">
                              <AlertTriangle className="size-4 text-content-negative-bold" />
                            </div>
                          ) : expiringSoon ? (
                            <div className="size-8 rounded-full bg-content-notice-bold/10 flex items-center justify-center shrink-0">
                              <AlertTriangle className="size-4 text-background-notice-bold" />
                            </div>
                          ) : (
                            <CircleCheck className="size-4 text-content-positive-default shrink-0" />
                          )}
                        </Card>
                      );
                    })}
                  </div>

                  {/* Add Document Button */}
                  <div className="pt-4">
                    <Button
                      onClick={() => {
                        hapticLight()
                        setShowAddDocDrawer(true)
                      }}
                      className="w-full"
                    >
                      <Plus className="size-4 mr-2" />
                      Add Document
                    </Button>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Flight History Tab */}
          <TabsContent value="flight-history">
            {!passenger.tripLegs || passenger.tripLegs.length === 0 ? (
              <EmptyStateCard label="No flight history" />
            ) : (
              <Card className="p-3">
                <div className="flex flex-col gap-3">
                  {passenger.tripLegs
                    .slice()
                    .sort((a, b) => dayjs(b.departureDateTime).valueOf() - dayjs(a.departureDateTime).valueOf())
                    .map((leg, index) => (
                      <div key={leg.id}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <PlaneTakeoff className="size-6 text-content-brand-subtle shrink-0" />
                            <p className="text-sm font-semibold shrink-0">{leg.departureAirportIcao}</p>
                            <p className="text-sm whitespace-nowrap">
                              {dayjs(leg.departureDateTime).format('D MMM')} &bull; {dayjs(leg.departureDateTime).format('HH:mm')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm whitespace-nowrap">
                              {dayjs(leg.arrivalDateTime).format('D MMM')} &bull; {dayjs(leg.arrivalDateTime).format('HH:mm')}
                            </p>
                            <p className="text-sm font-semibold shrink-0">{leg.arrivalAirportIcao}</p>
                            <PlaneLanding className="size-6 text-content-brand-subtle shrink-0" />
                          </div>
                        </div>
                        {index < passenger.tripLegs.length - 1 && (
                          <div className="border-t border-border-L2 mt-3" />
                        )}
                      </div>
                    ))}
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>

      {/* Add Document Drawer */}
      <DocumentFormDrawer
        isOpen={showAddDocDrawer}
        owner={{ type: 'passenger', passengerId }}
        onClose={() => setShowAddDocDrawer(false)}
      />

      {/* View Document Drawer */}
      <ViewDocumentDrawer
        isOpen={isViewDocOpen}
        onClose={() => setIsViewDocOpen(false)}
        documentId={viewDocId}
      />
    </>
  );

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title={fullName}
          breadcrumb={paxBreadcrumb}
          action={<PaxActionsSelect passengerId={passengerId} onPassengerDeleted={() => {}} />}
        />
        {adminBody}
      </DesktopPage>
    );
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          onBack={handleBack}
          rightElement={<PaxActionsSelect passengerId={passengerId} onPassengerDeleted={() => {}} />}
          title="PAX"
        />
      }
      className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
    >
      {adminBody}
    </AppLayout>
  );
}

export function PreferenceCard({
  icon,
  title,
  value,
  photos,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  // Optional photo content (e.g. a read-only image grid) rendered below the value.
  photos?: React.ReactNode;
}) {
  return (
    <Card className="px-4 py-4">
      <div className="flex flex-col gap-3">
        <div className="flex gap-2 items-center">
          <div className="size-8 rounded-full bg-background-brand-subtle flex items-center justify-center">
            {icon}
          </div>
          <p className="text-sm font-semibold text-content-primary-subtle">{title}</p>
        </div>
        {/* PRNTR textarea "Value" state: the value sits in a surface-L2 field box */}
        <p className="whitespace-pre-wrap wrap-break-word rounded-[10px] border border-border-L1 bg-surface-L2 p-3 text-sm font-semibold text-content-primary-bold">
          {value}
        </p>
        {photos}
      </div>
    </Card>
  );
}
